import React, { useState, useContext, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';

import get from 'lodash/get';
import map from 'lodash/map';
import find from 'lodash/find';
import join from 'lodash/join';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';

import { alpha } from '@mui/material/styles';
import {
    Grid, Box, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
    Typography, Collapse, InputBase, Select, MenuItem, Menu, ListItemIcon, ListItemText,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailIcon from '@mui/icons-material/Mail';
import CircularProgress from '@mui/material/CircularProgress';

import RoundPreviewModal from './RoundPreviewModal';
import MoveEventDialog from '../components/dialogs/MoveEventDialog';
import { DataContext } from '../components/data/DataContext';
import { DataContextType } from '../components/data_types/interfaces';
import { EventFormData, PlainObject, EventAccess } from '../components/data_types/types';
import { EventLocation, EventTransitionModel, ScoreExportFormat } from '../components/data_types/eventTypes';
import { formatEventDate } from '../components/data/formatting';
import { gridSpacing } from '../assets/theme/muitheme';
import { DS, Overline, PageHeader, enterSx, numericSx } from '../components/shared/Dashboard';
import { InvitePlayerInput, InvitedPlayersList } from '../components/shared/InvitePlayers';

import { DISC_BAG_NAMES, REWARD_OPTIONS, MOULD_NAMES, DISC_PROPERTY_TYPES } from '../components/data/disc_data';

function getDiscBag(bagVal: number) {
    return get(find(DISC_BAG_NAMES, ['value', bagVal]), 'label', 'Default');
};

function discObjToStr(disc: PlainObject) {
    const attrs = join(map(disc.discProperties, (attr) => {
        const a = find(DISC_PROPERTY_TYPES, ['value', attr]);
        return a ? a.label : 'Unknown';
    }), '/');

    const mould = get(find(MOULD_NAMES, ['value', disc.mouldName]), 'label', 'Unknown');
    const colorName = disc.colorsName;
    const stampVariant = disc.stampVariant;

    return `${attrs} ${mould} (<strong>Color:</strong> ${colorName} <strong>Stamp Variant:</strong> ${stampVariant})`;
};

function getEventRewards(event: EventFormData | PlainObject | undefined) {
    if (!event) {
        return 'None';
    }

    // return the type of reward's label
    if (includes([0, 1, 4, 5, 6, 7], event.participationReward)) {
        const rewardType = find(REWARD_OPTIONS, ['value', event.participationReward]);
        return rewardType ? rewardType.label : 'None';
    }

    // custom reward
    if (event.participationReward === 2) {
        // const { eventRewardCoins, eventRewardDiscs, eventRewardIcons } = event.customRewards;
        const { eventRewardDiscs } = event.customRewards;

        if (isEmpty(eventRewardDiscs) && event.customReward) {
            return discObjToStr(event.customReward);
        } else {
            return join(map(eventRewardDiscs, discObjToStr), ', ');
        }
    }

    return 'None';
};

/*****************************************************************************
 ** Tabs, timing and sorting
*****************************************************************************/

interface TabDef {
    label: string;
    location: EventLocation;
    color: string;
    showManageBtns: boolean;
    showConvertStateBtns: boolean;
    canAffectLockStatus: boolean;
    showMoveButton: boolean;
    showInvites: boolean;
}

// Order matters: the dashboard links to tabs by index (see STATUS_META in AdminDashboard)
// Round locking goes through cloud functions that only know public and private events, so invite events can't use it
const TABS: TabDef[] = [
    { label: 'Pending', location: EventLocation.PENDING, color: DS.warning, showManageBtns: true, showConvertStateBtns: true, canAffectLockStatus: false, showMoveButton: false, showInvites: false },
    { label: 'Private', location: EventLocation.PRIVATE, color: DS.info, showManageBtns: false, showConvertStateBtns: false, canAffectLockStatus: true, showMoveButton: true, showInvites: false },
    { label: 'Invite', location: EventLocation.INVITE, color: DS.violet, showManageBtns: false, showConvertStateBtns: false, canAffectLockStatus: false, showMoveButton: true, showInvites: true },
    { label: 'Public', location: EventLocation.PUBLIC, color: DS.gain, showManageBtns: false, showConvertStateBtns: false, canAffectLockStatus: true, showMoveButton: true, showInvites: false },
    { label: 'Archived', location: EventLocation.ARCHIVED, color: DS.neutral, showManageBtns: false, showConvertStateBtns: false, canAffectLockStatus: false, showMoveButton: true, showInvites: false },
];

type SortKey = 'start-desc' | 'start-asc' | 'name';

// Event times are stored and displayed as UTC (see formatEventDate)
const parseTime = (value?: string) => {
    if (!value) return undefined;
    const parsed = moment.utc(value);
    return parsed.isValid() ? parsed : undefined;
};

const getTiming = (event: EventFormData | PlainObject) => {
    const now = moment.utc();
    const start = parseTime(event.eventStartTime);
    const end = parseTime(event.eventEndTime);

    if (!start && !end) return { label: 'No dates', color: DS.textFaint };
    if (start && start.isAfter(now)) return { label: `Starts ${start.fromNow()}`, color: DS.info };
    if (end && end.isBefore(now)) return { label: `Ended ${end.fromNow()}`, color: DS.neutral };
    return { label: end ? `Live · ends ${end.fromNow()}` : 'Live', color: DS.gain };
};

const sortEvents = (events: (EventFormData | PlainObject)[], sortKey: SortKey) => {
    const startOf = (e: PlainObject) => parseTime(e.eventStartTime)?.valueOf() ?? 0;
    return [...events].sort((a, b) => {
        if (sortKey === 'name') return String(a.eventName || '').localeCompare(String(b.eventName || ''));
        return sortKey === 'start-asc' ? startOf(a) - startOf(b) : startOf(b) - startOf(a);
    });
};

/*****************************************************************************
 ** Small building blocks
*****************************************************************************/

const Pill: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
    <Box component="span" sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: '2px',
        borderRadius: '999px',
        background: alpha(color, 0.14),
        color,
        fontSize: '12px',
        fontWeight: 700,
        whiteSpace: 'nowrap',
    }}>
        {children}
    </Box>
);

const iconBtnSx = {
    color: DS.textMuted,
    border: `1px solid ${DS.border}`,
    borderRadius: '10px',
    '&:hover': { color: DS.text, background: DS.inset, borderColor: DS.borderStrong },
};

const DetailItem: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ minWidth: 0 }}>
        <Overline>{label}</Overline>
        <Typography component="div" sx={{ fontSize: '13px', color: DS.text, mt: 0.25, '& strong': { color: DS.textMuted, fontWeight: 600 } }}>
            {children}
        </Typography>
    </Box>
);

const EventCodeChip: React.FC<{ code: string }> = ({ code }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (error) {
            console.error('Could not copy event code:', error);
        }
    };

    return (
        <Tooltip title={copied ? 'Copied!' : 'Copy event code'}>
            <Box component="button" onClick={handleCopy} sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                py: '2px',
                border: `1px solid ${DS.border}`,
                borderRadius: '8px',
                background: DS.inset,
                color: DS.textMuted,
                fontFamily: 'monospace',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { color: DS.text, borderColor: DS.borderStrong },
            }}>
                {code}
                {copied ? <CheckIcon sx={{ fontSize: 13, color: DS.gain }} /> : <ContentCopyIcon sx={{ fontSize: 13 }} />}
            </Box>
        </Tooltip>
    );
};

/*****************************************************************************
 ** Event list for one tab
*****************************************************************************/

interface EventTypeListProps {
    events: (EventFormData | PlainObject)[];
    isAdmin: boolean;
    isCreator: boolean;
    showManageBtns: boolean;
    showConvertStateBtns: boolean;
    canAffectLockStatus: boolean;
    showMoveButton: boolean;
    showInvites: boolean;
    currentEventType: EventLocation;
    emptyText: string;
}

function EventTypeList(props: EventTypeListProps) {
    const navigate = useNavigate();

    const context = useContext<DataContextType | undefined>(DataContext);
    const { userId, moveEventToSelectedNode, eventSetRoundStatus, exportEventScores, inviteRosters, invitePlayersToEvent, revokeEventInvites } = context || {};

    const { events, isAdmin, isCreator, showManageBtns, showConvertStateBtns, canAffectLockStatus, showMoveButton, showInvites, currentEventType, emptyText } = props;

    const [selectedRound, setSelectedRound] = useState<PlainObject | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<EventFormData | PlainObject | null>(null);
    const [loadingRound, setLoadingRound] = useState<{ eventID: string, roundNumber: number } | null>(null);
    const [loadingPrivateEvent, setLoadingPrivateEvent] = useState<string | null>(null);
    const [loadingMoveEvent, setLoadingMoveEvent] = useState<string | null>(null);
    const [moveEventDialogOpen, setMoveEventDialogOpen] = useState<boolean>(false);
    const [selectedEventForMove, setSelectedEventForMove] = useState<EventFormData | PlainObject | null>(null);
    const [loadingExportEvent, setLoadingExportEvent] = useState<string | null>(null);
    const [exportMenu, setExportMenu] = useState<{ anchor: HTMLElement; event: EventFormData | PlainObject } | null>(null);
    const [noScoresDialogOpen, setNoScoresDialogOpen] = useState<boolean>(false);
    const [currentEventName, setCurrentEventName] = useState<string>('');
    const [expandedRounds, setExpandedRounds] = useState<{ [eventId: string]: boolean }>({});
    const [expandedInvites, setExpandedInvites] = useState<{ [eventId: string]: boolean }>({});
    const [invitingEvent, setInvitingEvent] = useState<string | null>(null);
    const [revokingInvite, setRevokingInvite] = useState<{ eventID: string, playerId: string } | null>(null);

    const handleExportEventScores = async (event: EventFormData | PlainObject, format: ScoreExportFormat) => {
        setExportMenu(null);
        if (exportEventScores) {
            setLoadingExportEvent(event.eventID);
            try {
                const result = await exportEventScores(event.eventID, event.eventName, format);
                if (!result.success) {
                    if (result.error === 'NO_SCORES_FOUND') {
                        setCurrentEventName(event.eventName);
                        setNoScoresDialogOpen(true);
                    } else {
                        console.error('Error exporting event scores:', result.error);
                        // Handle other errors (could add more specific dialogs here)
                    }
                }
            } catch (error) {
                console.error('Unexpected error exporting event scores:', error);
            } finally {
                setLoadingExportEvent(null);
            }
        }
    };

    const handleCloseNoScoresDialog = () => {
        setNoScoresDialogOpen(false);
        setCurrentEventName('');
    };

    const handleRoundLockClick = async (event: EventFormData | PlainObject, roundNumber: number, lock: boolean) => {
        if (eventSetRoundStatus) {
            setLoadingRound({ eventID: event.eventID, roundNumber }); // Start loading
            const isPublic = event.eventAccess === EventAccess.Public;
            await eventSetRoundStatus(event.eventID, roundNumber, isPublic, lock);
            setLoadingRound(null); // Stop loading
        }
    };

    // Pending events go live as private, or as invite only when they were created that way
    const handleMakeEventLive = async (event: EventFormData | PlainObject, destination: EventLocation) => {
        if (moveEventToSelectedNode) {
            setLoadingPrivateEvent(event.eventID); // Start loading
            const eventTransition: EventTransitionModel = {
                eventId: event.eventID,
                source: { location: currentEventType },
                destination: { location: destination }
            };
            try {
                await moveEventToSelectedNode(eventTransition);
            } catch (error) {
                console.error('Error making event live:', error);
                alert(`Could not move "${event.eventName}": ${(error as Error).message}`);
            } finally {
                setLoadingPrivateEvent(null); // Stop loading
            }
        }
    };

    const handleInvitePlayers = async (event: EventFormData | PlainObject, playerIds: string[]) => {
        if (invitePlayersToEvent) {
            setInvitingEvent(event.eventID);
            try {
                await invitePlayersToEvent(event.eventID, playerIds);
            } catch (error) {
                console.error('Error inviting players:', error);
                alert(`Could not invite players: ${(error as Error).message}`);
            } finally {
                setInvitingEvent(null);
            }
        }
    };

    const handleRevokeInvite = async (event: EventFormData | PlainObject, playerId: string) => {
        if (revokeEventInvites) {
            setRevokingInvite({ eventID: event.eventID, playerId });
            try {
                await revokeEventInvites(event.eventID, [playerId]);
            } catch (error) {
                console.error('Error revoking invite:', error);
                alert(`Could not revoke the invite: ${(error as Error).message}`);
            } finally {
                setRevokingInvite(null);
            }
        }
    };

    const handleOpenMoveDialog = (event: EventFormData | PlainObject) => {
        setSelectedEventForMove(event);
        setMoveEventDialogOpen(true);
    };

    const handleCloseMoveDialog = () => {
        setMoveEventDialogOpen(false);
        setSelectedEventForMove(null);
    };

    const handleMoveEvent = async (targetLocation: EventLocation) => {
        if (moveEventToSelectedNode && selectedEventForMove) {
            setLoadingMoveEvent(selectedEventForMove.eventID); // Start loading
            const eventTransition: EventTransitionModel = {
                eventId: selectedEventForMove.eventID,
                source: { location: currentEventType },
                destination: { location: targetLocation }
            };
            try {
                await moveEventToSelectedNode(eventTransition);
            } catch (error) {
                console.error('Error moving event:', error);
                alert(`Could not move "${selectedEventForMove.eventName}": ${(error as Error).message}`);
            } finally {
                setLoadingMoveEvent(null); // Stop loading
            }
        }
    };

    // Owners can send their own private events back to pending or archive them; admins can move any event anywhere
    const OWNER_DESTINATIONS = [EventLocation.PENDING, EventLocation.ARCHIVED];
    const isOwnPrivateEvent = (event: EventFormData | PlainObject) =>
        currentEventType === EventLocation.PRIVATE && !isAdmin && isCreator && !!userId && event.createdBy === userId;
    const canMoveEvent = (event: EventFormData | PlainObject) => (showMoveButton && isAdmin) || isOwnPrivateEvent(event);

    const getOwnerMoveNote = (target: EventLocation) =>
        target === EventLocation.ARCHIVED ? 'Only an admin can move it back out of Archived.'
        : target === EventLocation.PENDING ? 'Back to Pending so it can be edited again. An admin needs to make it private again.'
        : undefined;

    if (isEmpty(events)) {
        return (
            <Box sx={{
                textAlign: 'center',
                py: 6,
                background: DS.surface,
                border: `1px dashed ${DS.borderStrong}`,
                borderRadius: DS.radius,
            }}>
                <EventIcon sx={{ fontSize: 32, color: DS.textFaint, mb: 1 }} />
                <Typography sx={{ fontWeight: 700, color: DS.text }}>No events found</Typography>
                <Typography sx={{ fontSize: '13px', color: DS.textMuted, mt: 0.5 }}>{emptyText}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <RoundPreviewModal
                round={selectedRound}
                event={selectedEvent}
                isOpen={!!selectedRound}
                onClose={() => {
                    setSelectedRound(null);
                    setSelectedEvent(null);
                }}
            />

            <MoveEventDialog
                open={moveEventDialogOpen}
                onClose={handleCloseMoveDialog}
                onMove={handleMoveEvent}
                eventName={selectedEventForMove?.eventName || ''}
                currentType={currentEventType}
                loading={loadingMoveEvent === selectedEventForMove?.eventID}
                allowedDestinations={isAdmin ? undefined : OWNER_DESTINATIONS}
                getDestinationNote={isAdmin ? undefined : getOwnerMoveNote}
            />

            <Dialog
                open={noScoresDialogOpen}
                onClose={handleCloseNoScoresDialog}
                maxWidth="sm"
                fullWidth
                aria-labelledby="no-scores-dialog-title"
                aria-describedby="no-scores-dialog-description"
            >
                <DialogTitle id="no-scores-dialog-title">
                    No Scores Found
                </DialogTitle>
                <DialogContent>
                    <Typography id="no-scores-dialog-description" sx={{ mb: 2 }}>
                        No score data was found for the event "{currentEventName}".
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        This could happen if:
                    </Typography>
                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                        <li>No players have submitted scores yet</li>
                        <li>All rounds are still in progress</li>
                        <li>The event has not started</li>
                        <li>There was an issue with data synchronization</li>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseNoScoresDialog}
                        variant="contained"
                        color="primary"
                        autoFocus
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <Menu
                anchorEl={exportMenu?.anchor}
                open={!!exportMenu}
                onClose={() => setExportMenu(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => exportMenu && handleExportEventScores(exportMenu.event, 'csv')}>
                    <ListItemIcon><TableChartIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Summary (.csv)" secondary="Totals per player and round" />
                </MenuItem>
                <MenuItem onClick={() => exportMenu && handleExportEventScores(exportMenu.event, 'json')}>
                    <ListItemIcon><DataObjectIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Raw scores (.json)" secondary="All score data as stored in Firebase" />
                </MenuItem>
            </Menu>

            {
                map(events, (event: EventFormData | PlainObject, eventIdx: number) => {
                    const timing = getTiming(event);
                    const rounds: PlainObject[] = event.rounds || [];
                    const lockedCount = rounds.filter((r) => r.locked).length;
                    const isExpanded = !!expandedRounds[event.eventID];
                    // All creators can edit pending events; making them private stays admin-only
                    const canEditEvent = showManageBtns && (isAdmin || isCreator);
                    const canMakePrivate = showManageBtns && showConvertStateBtns && isAdmin;
                    const isInviteEvent = event.eventAccess === EventAccess.Invite;
                    const liveDestination = isInviteEvent ? EventLocation.INVITE : EventLocation.PRIVATE;
                    const draftInviteCount = (event.invitedPlayers || []).length;
                    const invitedPlayerIds = Object.keys(inviteRosters?.[event.eventID] || {});
                    const isInvitesExpanded = !!expandedInvites[event.eventID];

                    return (
                        <Box key={event.eventID} sx={{
                            background: DS.surface,
                            border: `1px solid ${DS.border}`,
                            borderRadius: DS.radius,
                            overflow: 'hidden',
                            transition: 'border-color 0.2s ease',
                            '&:hover': { borderColor: DS.borderStrong },
                            ...enterSx(Math.min(eventIdx, 8) * 0.03),
                        }}>
                            {/* Event header */}
                            <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 }, pb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '18px', fontWeight: 800, letterSpacing: '-0.01em', color: DS.text, wordBreak: 'break-word' }}>
                                            {event.eventName}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                                            <EventCodeChip code={event.eventID} />
                                            <Pill color={timing.color}>{timing.label}</Pill>
                                            {!!userId && event.createdBy === userId && <Pill color={DS.info}>Yours</Pill>}
                                            {currentEventType === EventLocation.PENDING && isInviteEvent && (
                                                <Pill color={DS.violet}>
                                                    <MailIcon sx={{ fontSize: 12 }} />
                                                    Invite only · {draftInviteCount} {draftInviteCount === 1 ? 'player' : 'players'}
                                                </Pill>
                                            )}
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                        {canMoveEvent(event) && (
                                            <Tooltip title="Move Event">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenMoveDialog(event);
                                                        }}
                                                        disabled={loadingMoveEvent === event.eventID}
                                                        sx={iconBtnSx}
                                                    >
                                                        {loadingMoveEvent === event.eventID ? (
                                                            <CircularProgress size={18} />
                                                        ) : (
                                                            <DriveFileMoveIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Export Event Scores">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExportMenu({ anchor: e.currentTarget, event });
                                                    }}
                                                    disabled={loadingExportEvent === event.eventID}
                                                    sx={iconBtnSx}
                                                >
                                                    {loadingExportEvent === event.eventID ? (
                                                        <CircularProgress size={18} />
                                                    ) : (
                                                        <FileDownloadIcon fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                {/* Event details */}
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: '2fr 1fr 1fr' },
                                    gap: 2,
                                    mt: 2,
                                    p: 2,
                                    borderRadius: '12px',
                                    background: DS.inset,
                                }}>
                                    <DetailItem label="Reward">
                                        <span dangerouslySetInnerHTML={{ __html: getEventRewards(event) }} />
                                    </DetailItem>
                                    {event.eventStartTime !== '' && (
                                        <DetailItem label="Event start">{formatEventDate(event.eventStartTime)}</DetailItem>
                                    )}
                                    {event.eventEndTime !== '' && (
                                        <DetailItem label="Event end">{formatEventDate(event.eventEndTime)}</DetailItem>
                                    )}
                                </Box>
                            </Box>

                            {/* Rounds */}
                            <Box sx={{ borderTop: `1px solid ${DS.border}` }}>
                                <Box
                                    onClick={() => {
                                        setExpandedRounds(prev => ({
                                            ...prev,
                                            [event.eventID]: !prev[event.eventID]
                                        }));
                                    }}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        px: { xs: 2, sm: 3 },
                                        py: 1.5,
                                        cursor: 'pointer',
                                        '&:hover': { background: DS.inset },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: DS.text }}>
                                            Rounds ({rounds.length})
                                        </Typography>
                                        {canAffectLockStatus && rounds.length > 0 && (
                                            <Typography sx={{ fontSize: '12px', color: DS.textMuted, ...numericSx }}>
                                                {rounds.length - lockedCount} open · {lockedCount} locked
                                            </Typography>
                                        )}
                                    </Box>
                                    <ExpandMoreIcon sx={{
                                        color: DS.textMuted,
                                        transition: 'transform 0.2s ease',
                                        transform: isExpanded ? 'rotate(180deg)' : 'none',
                                    }} />
                                </Box>

                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2.5 }, pt: 0.5 }}>
                                        <Grid container spacing={1.5}>
                                            {
                                                map(rounds, (round: PlainObject, idx: number) => {
                                                    const roundId = idx + 1;
                                                    const isRoundLoading = loadingRound?.eventID === event.eventID && loadingRound?.roundNumber === roundId;

                                                    return (
                                                        <Grid key={idx} item xs={12} sm={6} md={4} lg={3}>
                                                            <Box
                                                                onClick={() => {
                                                                    setSelectedRound(round);
                                                                    setSelectedEvent(event);
                                                                }}
                                                                sx={{
                                                                    height: '100%',
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 1,
                                                                    p: 1.75,
                                                                    borderRadius: '12px',
                                                                    border: `1px solid ${DS.border}`,
                                                                    background: DS.inset,
                                                                    cursor: 'pointer',
                                                                    transition: 'border-color 0.2s ease',
                                                                    '&:hover': { borderColor: alpha(DS.brand, 0.5) },
                                                                }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                                    <Overline>Round {roundId}</Overline>
                                                                    {canAffectLockStatus && (
                                                                        <Pill color={round.locked ? DS.warning : DS.gain}>
                                                                            {round.locked ? 'Locked' : 'Open'}
                                                                        </Pill>
                                                                    )}
                                                                </Box>
                                                                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: DS.text }}>
                                                                    {round.courseName}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>
                                                                    {getDiscBag(round.discBag)}
                                                                    {round.holes && ` · ${round.holes.length} holes`}
                                                                </Typography>

                                                                {canAffectLockStatus && (
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        color={round.locked ? 'success' : 'error'}
                                                                        onClick={async (e) => {
                                                                            e.stopPropagation();
                                                                            await handleRoundLockClick(event, roundId, !round.locked);
                                                                        }}
                                                                        disabled={isRoundLoading}
                                                                        startIcon={
                                                                            isRoundLoading ?
                                                                                <CircularProgress size={14} />
                                                                                : round.locked ? <LockOpenIcon sx={{ fontSize: '1rem' }} />
                                                                                : <LockIcon sx={{ fontSize: '1rem' }} />
                                                                        }
                                                                        sx={{ mt: 'auto', borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                                                                    >
                                                                        {isRoundLoading ? '' : round.locked ? 'Unlock' : 'Lock'}
                                                                    </Button>
                                                                )}
                                                            </Box>
                                                        </Grid>
                                                    );
                                                })
                                            }
                                        </Grid>
                                    </Box>
                                </Collapse>
                            </Box>

                            {/* Invited players */}
                            {showInvites && isAdmin && (
                                <Box sx={{ borderTop: `1px solid ${DS.border}` }}>
                                    <Box
                                        onClick={() => {
                                            setExpandedInvites(prev => ({
                                                ...prev,
                                                [event.eventID]: !prev[event.eventID]
                                            }));
                                        }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            px: { xs: 2, sm: 3 },
                                            py: 1.5,
                                            cursor: 'pointer',
                                            '&:hover': { background: DS.inset },
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: DS.text }}>
                                            Invited players ({invitedPlayerIds.length})
                                        </Typography>
                                        <ExpandMoreIcon sx={{
                                            color: DS.textMuted,
                                            transition: 'transform 0.2s ease',
                                            transform: isInvitesExpanded ? 'rotate(180deg)' : 'none',
                                        }} />
                                    </Box>

                                    <Collapse in={isInvitesExpanded} timeout="auto" unmountOnExit>
                                        <Box sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2.5 }, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            <InvitePlayerInput
                                                existing={invitedPlayerIds}
                                                busy={invitingEvent === event.eventID}
                                                buttonLabel="Invite"
                                                onAdd={(ids) => handleInvitePlayers(event, ids)}
                                            />
                                            <InvitedPlayersList
                                                playerIds={invitedPlayerIds}
                                                onRemove={(playerId) => handleRevokeInvite(event, playerId)}
                                                removingId={revokingInvite?.eventID === event.eventID ? revokingInvite?.playerId : null}
                                                removeLabel="Revoke invite"
                                                emptyText="Nobody is invited yet, so no one can see this event."
                                            />
                                        </Box>
                                    </Collapse>
                                </Box>
                            )}

                            {/* Manage actions */}
                            {(canEditEvent || canMakePrivate) && (
                                <Box sx={{
                                    display: 'flex',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                    px: { xs: 2, sm: 3 },
                                    py: 1.5,
                                    borderTop: `1px solid ${DS.border}`,
                                    background: DS.inset,
                                }}>
                                    {canEditEvent && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<EditIcon />}
                                            onClick={() => {
                                                navigate(`/admin/edit-event/${event.eventID}`);
                                            }}
                                            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 700, px: 2 }}
                                        >
                                            Edit Event
                                        </Button>
                                    )}

                                    {canMakePrivate && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="secondary"
                                            startIcon={loadingPrivateEvent === event.eventID ? undefined : isInviteEvent ? <MailIcon /> : <LockIcon />}
                                            onClick={async () => {
                                                await handleMakeEventLive(event, liveDestination);
                                            }}
                                            disabled={loadingPrivateEvent === event.eventID}
                                            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 700, px: 2, minWidth: '120px' }}
                                        >
                                            {loadingPrivateEvent === event.eventID ? (
                                                <CircularProgress size={16} />
                                            ) : isInviteEvent ? (
                                                draftInviteCount > 0 ? `Make Invite-only & invite ${draftInviteCount}` : 'Make Invite-only'
                                            ) : (
                                                'Make Private'
                                            )}
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                    );
                })
            }
        </Box>
    );
};

/*****************************************************************************
 ** Page
*****************************************************************************/

const EventsList = () => {
    const navigate = useNavigate();
    const context = useContext<DataContextType | undefined>(DataContext);
    const { isAdmin, isCreator, pendingEvents, privateEvents, inviteEvents, publicEvents, archivedEvents } = context || {};

    const location = useLocation();
    const [tabIndex, setTabIndex] = useState<number>((location.state as { tab?: number } | null)?.tab ?? 0);
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState<SortKey>('start-desc');

    const eventsByTab = [pendingEvents, privateEvents, inviteEvents, publicEvents, archivedEvents];
    const counts = eventsByTab.map((events) => Object.keys(events || {}).length);
    const activeTab = TABS[tabIndex] ?? TABS[0];

    const visibleEvents = useMemo(() => {
        const events = Object.values(eventsByTab[tabIndex] || {}) as (EventFormData | PlainObject)[];
        const query = search.trim().toLowerCase();
        const filtered = query
            ? events.filter((e) =>
                String(e.eventName || '').toLowerCase().includes(query) ||
                String(e.eventID || '').toLowerCase().includes(query))
            : events;
        return sortEvents(filtered, sortKey);
    }, [tabIndex, search, sortKey, pendingEvents, privateEvents, inviteEvents, publicEvents, archivedEvents]); // eslint-disable-line react-hooks/exhaustive-deps

    const emptyText = search.trim()
        ? `No ${activeTab.label.toLowerCase()} events match "${search.trim()}".`
        : 'There are no events in this category yet.';

    return (
        <Grid container spacing={gridSpacing} sx={{ mb: 6 }}>
            <PageHeader
                overline="Admin"
                title="Event management"
                subtitle="Manage all your disc golf events across different states and categories."
                action={
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        disabled={!isAdmin && !isCreator}
                        onClick={() => navigate('/admin/add-event')}
                        sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 700, px: 2.5 }}
                    >
                        New event
                    </Button>
                }
            />

            {/* Tabs + toolbar */}
            <Grid item xs={12} sx={enterSx(0.05)}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box role="tablist" sx={{
                        display: 'flex',
                        gap: 0.5,
                        p: 0.5,
                        background: DS.surface,
                        border: `1px solid ${DS.border}`,
                        borderRadius: '14px',
                        overflowX: 'auto',
                        maxWidth: '100%',
                    }}>
                        {TABS.map((tab, index) => {
                            const selected = index === tabIndex;
                            return (
                                <Box
                                    key={tab.label}
                                    component="button"
                                    role="tab"
                                    aria-selected={selected}
                                    onClick={() => setTabIndex(index)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 1.75,
                                        py: 0.9,
                                        border: `1px solid ${selected ? alpha(tab.color, 0.3) : 'transparent'}`,
                                        borderRadius: '10px',
                                        background: selected ? alpha(tab.color, 0.12) : 'transparent',
                                        color: selected ? tab.color : DS.textMuted,
                                        fontSize: '14px',
                                        fontWeight: selected ? 700 : 600,
                                        fontFamily: 'inherit',
                                        whiteSpace: 'nowrap',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        '&:hover': selected ? {} : { color: DS.text, background: DS.inset },
                                    }}
                                >
                                    {tab.label}
                                    <Box component="span" sx={{
                                        minWidth: 20,
                                        px: 0.75,
                                        borderRadius: '999px',
                                        background: counts[index] > 0 ? alpha(tab.color, 0.18) : DS.inset,
                                        color: counts[index] > 0 ? tab.color : DS.textFaint,
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        lineHeight: '18px',
                                        textAlign: 'center',
                                        ...numericSx,
                                    }}>
                                        {counts[index]}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flex: { xs: '1 1 100%', md: '0 1 auto' } }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            flex: 1,
                            minWidth: { md: 220 },
                            background: DS.surface,
                            border: `1px solid ${DS.border}`,
                            borderRadius: '12px',
                            '&:focus-within': { borderColor: alpha(DS.brand, 0.5) },
                        }}>
                            <SearchIcon sx={{ fontSize: 18, color: DS.textFaint }} />
                            <InputBase
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search name or code"
                                inputProps={{ 'aria-label': 'Search events' }}
                                sx={{ flex: 1, fontSize: '14px', color: DS.text, py: 0.75 }}
                            />
                        </Box>
                        <Select
                            size="small"
                            value={sortKey}
                            onChange={(e) => setSortKey(e.target.value as SortKey)}
                            inputProps={{ 'aria-label': 'Sort events' }}
                            sx={{
                                fontSize: '14px',
                                borderRadius: '12px',
                                background: DS.surface,
                                color: DS.textMuted,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: DS.border },
                            }}
                        >
                            <MenuItem value="start-desc">Event start · latest first</MenuItem>
                            <MenuItem value="start-asc">Event start · earliest first</MenuItem>
                            <MenuItem value="name">Name A–Z</MenuItem>
                        </Select>
                    </Box>
                </Box>
            </Grid>

            <Grid item xs={12} role="tabpanel">
                <EventTypeList
                    key={activeTab.location}
                    events={visibleEvents}
                    isAdmin={!!isAdmin}
                    isCreator={!!isCreator}
                    showManageBtns={activeTab.showManageBtns}
                    showConvertStateBtns={activeTab.showConvertStateBtns}
                    canAffectLockStatus={activeTab.canAffectLockStatus}
                    showMoveButton={activeTab.showMoveButton}
                    showInvites={activeTab.showInvites}
                    currentEventType={activeTab.location}
                    emptyText={emptyText}
                />
            </Grid>
        </Grid>
    );
};

export default EventsList;
